import * as React from 'react';
import {
    Alert, Box, Breadcrumbs, Button, CircularProgress, FormControlLabel, Link as MuiLink, Stack, Switch, Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { AI_LABELS, getAiRun } from '../../Api';
import { AiRunDetailDto } from '../../models/dtos/AiDto';
import AiRunsGrid from '../../components/ai/AiRunsGrid';
import AiRunDetailView from '../../components/ai/AiRunDetailView';
import { AiActionError, AiErrorAlert, aiLoadErrorText, AiRunStatusChip } from '../../components/ai/AiShared';
import { AiExportOutcome, AiRunExportButton } from '../../components/ai/AiBundleExport';

export default function AiRunsPage() {
    const [reloadKey, setReloadKey] = React.useState(0);
    return (
        <Stack gap={2} sx={{ mx: 5, my: 5 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                    <Typography variant="h4">AI agent runs</Typography>
                    <Typography variant="body2" color="text.secondary">Every agent run with its status, cost and turns. Click a run for the full step timeline.</Typography>
                </Box>
                <Button startIcon={<RefreshIcon />} onClick={() => setReloadKey(k => k + 1)}>Refresh</Button>
            </Stack>
            <AiRunsGrid reloadKey={reloadKey} />
        </Stack>
    );
}

const POLL_MS = 1500;

export function AiRunPage() {
    const { runId } = useParams<{ runId: string }>();
    const [run, setRun] = React.useState<AiRunDetailDto | null>(null);
    const [error, setError] = React.useState<string | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [autoRefresh, setAutoRefresh] = React.useState(true);
    const [exportError, setExportError] = React.useState<AiActionError | null>(null);
    const [exportNotice, setExportNotice] = React.useState<string | null>(null);
    const onExport = (outcome: AiExportOutcome) => {
        setExportError(outcome.error);
        setExportNotice(outcome.notice);
    };

    const load = React.useCallback(async () => {
        if (!runId) return;
        const detail = await getAiRun(runId);
        setLoading(false);
        if (detail == null) {
            setError(aiLoadErrorText(AI_LABELS.run, `Failed to load run ${runId}.`));
            return;
        }
        setError(null);
        setRun(detail);
    }, [runId]);

    React.useEffect(() => {
        setLoading(true);
        setRun(null);
        load();
    }, [load]);

    const active = run != null && (run.status === 'QUEUED' || run.status === 'RUNNING');
    React.useEffect(() => {
        if (!active || !autoRefresh) return;
        const timer = window.setTimeout(load, POLL_MS);
        return () => window.clearTimeout(timer);
    }, [active, autoRefresh, load, run]);

    return (
        <Stack gap={2} sx={{ mx: 5, my: 4 }}>
            <Breadcrumbs>
                <MuiLink component={RouterLink} to="/ai" underline="hover">AI</MuiLink>
                <MuiLink component={RouterLink} to="/ai/runs" underline="hover">Runs</MuiLink>
                <Typography color="text.primary" sx={{ fontFamily: 'monospace' }}>{runId}</Typography>
            </Breadcrumbs>
            <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                <Stack direction="row" gap={1.5} alignItems="center">
                    <Typography variant="h5">Run {run?.agentCode ? `· ${run.agentCode}` : ''}</Typography>
                    {run && <AiRunStatusChip status={run.status} />}
                    {active && <CircularProgress size={18} />}
                </Stack>
                <Stack direction="row" gap={1} alignItems="center">
                    {active && <FormControlLabel control={<Switch checked={autoRefresh} onChange={e => setAutoRefresh(e.target.checked)} />} label="Auto-refresh" />}
                    {runId && <AiRunExportButton runId={runId} onOutcome={onExport} />}
                    <Button startIcon={<RefreshIcon />} onClick={load}>Refresh</Button>
                </Stack>
            </Stack>
            {error && <Alert severity="error">{error}</Alert>}
            <AiErrorAlert error={exportError} onClose={() => setExportError(null)} />
            {exportNotice && <Alert severity="success" onClose={() => setExportNotice(null)}>{exportNotice}</Alert>}
            {loading && run == null && <CircularProgress />}
            {run && <AiRunDetailView run={run} />}
        </Stack>
    );
}
